import { useCallback, useRef, useEffect } from 'react';
import {
  ErrorTelemetry,
  UserActivityTelemetry,
  PerformanceTelemetry,
  BusinessEventTelemetry,
  TelemetryData,
  TelemetryBatchRequest
} from '../types/telemetry';

interface TelemetryConfig {
  apiUrl?: string;
  batchSize?: number;
  flushInterval?: number;
  enabled?: boolean;
  environment?: 'development' | 'staging' | 'production';
  version?: string;
}

interface TelemetryHook {
  logError: (error: Omit<ErrorTelemetry['error'], 'severity'> & { severity?: ErrorTelemetry['error']['severity'] }, context?: any) => void;
  logActivity: (activity: UserActivityTelemetry['activity']) => void;
  logPerformance: (performance: PerformanceTelemetry['performance']) => void;
  logBusinessEvent: (event: BusinessEventTelemetry['event']) => void;
  flush: () => Promise<void>;
  isEnabled: boolean;
}

const generateSessionId = (): string => {
  return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getOrCreateSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem('telemetry_session_id');
    if (existing) return existing;
    const id = generateSessionId();
    sessionStorage.setItem('telemetry_session_id', id);
    return id;
  } catch {
    return generateSessionId();
  }
};

const getUserId = (): string | undefined => {
  try {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId && storedUserId !== 'undefined' && storedUserId !== 'null') {
      return storedUserId;
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      return parsedUser?.id || parsedUser?.userId;
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const getAuthToken = (): string | undefined => {
  try {
    return localStorage.getItem('accessToken') || localStorage.getItem('token') || undefined;
  } catch {
    return undefined;
  }
};

export const useTelemetry = (config: TelemetryConfig = {}): TelemetryHook => {
  const {
    apiUrl = `${process.env.REACT_APP_API_URL}/api/telemetry`,
    batchSize = 20,
    flushInterval = 30000, // 30 seconds
    enabled = process.env.REACT_APP_TELEMETRY_ENABLED === 'true' || process.env.NODE_ENV === 'production',
    environment = (['development', 'staging', 'production'].includes(process.env.NODE_ENV || '')
      ? process.env.NODE_ENV
      : 'development') as 'development' | 'staging' | 'production',
    version = process.env.REACT_APP_VERSION
  } = config;

  const sessionIdRef = useRef<string>(getOrCreateSessionId());

  const eventQueueRef = useRef<TelemetryData[]>([]);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createBaseTelemetryData = useCallback(() => ({
    sessionId: sessionIdRef.current,
    userId: getUserId(),
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    referrer: document.referrer || undefined,
    environment,
    version
  }), [environment, version]);

  const sendEvents = useCallback(async (events: TelemetryData[]): Promise<void> => {
    if (!enabled || events.length === 0) return;
    const authToken = getAuthToken();
    if (!authToken) {
      return;
    }
    const payload: TelemetryBatchRequest = {
      events,
      metadata: {
        source: 'web',
        sdkVersion: '1.0.0'
      }
    };
    try {
      const response = await fetch(`${apiUrl}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        void errorData; // Swallow errors silently; telemetry should never break UX
      }
    } catch (error) {
      void error;
    }
  }, [enabled, apiUrl]);

  const flush = useCallback(async (): Promise<void> => {
    if (eventQueueRef.current.length === 0) return;
    const eventsToSend = [...eventQueueRef.current];
    eventQueueRef.current = [];
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    await sendEvents(eventsToSend);
  }, [sendEvents]);

  const queueEvent = useCallback((event: TelemetryData): void => {
    if (!enabled) return;
    eventQueueRef.current.push(event);
    if (eventQueueRef.current.length >= batchSize) {
      void flush();
      return;
    }
    if (!flushTimeoutRef.current) {
      flushTimeoutRef.current = setTimeout(flush, flushInterval);
    }
  }, [enabled, batchSize, flushInterval, flush]);

  const logError = useCallback((
    error: Omit<ErrorTelemetry['error'], 'severity'> & { severity?: ErrorTelemetry['error']['severity'] },
    context?: any
  ): void => {
    const errorEvent: ErrorTelemetry = {
      ...createBaseTelemetryData(),
      type: 'error',
      error: {
        severity: 'medium',
        ...error
      },
      ...(context && { context })
    };
    queueEvent(errorEvent);
  }, [createBaseTelemetryData, queueEvent]);

  const logActivity = useCallback((activity: UserActivityTelemetry['activity']): void => {
    const activityEvent: UserActivityTelemetry = {
      ...createBaseTelemetryData(),
      type: 'activity',
      activity
    };
    queueEvent(activityEvent);
  }, [createBaseTelemetryData, queueEvent]);

  const logPerformance = useCallback((performance: PerformanceTelemetry['performance']): void => {
    const performanceEvent: PerformanceTelemetry = {
      ...createBaseTelemetryData(),
      type: 'performance',
      performance
    };
    queueEvent(performanceEvent);
  }, [createBaseTelemetryData, queueEvent]);

  const logBusinessEvent = useCallback((event: BusinessEventTelemetry['event']): void => {
    const businessEvent: BusinessEventTelemetry = {
      ...createBaseTelemetryData(),
      type: 'business_event',
      event
    };
    queueEvent(businessEvent);
  }, [createBaseTelemetryData, queueEvent]);

  useEffect(() => {
    if (!enabled) return;
    const handleError = (event: ErrorEvent): void => {
      logError({
        name: event.error?.name || 'Error',
        message: event.message || 'Unknown error',
        stack: event.error?.stack,
        component: 'Global',
        severity: 'high'
      });
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
      const error = event.reason;
      logError({
        name: error?.name || 'UnhandledPromiseRejection',
        message: error?.message || 'Unhandled promise rejection',
        stack: error?.stack,
        component: 'Global',
        severity: 'high'
      });
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [enabled, logError]);

  useEffect(() => {
    if (!enabled || !window.performance) return;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          logPerformance({
            metric: 'page_load',
            value: navEntry.loadEventEnd - navEntry.startTime,
            unit: 'ms',
            route: window.location.pathname,
            additionalMetrics: {
              domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.startTime,
              firstPaint: navEntry.responseEnd - navEntry.requestStart,
              dns: navEntry.domainLookupEnd - navEntry.domainLookupStart,
              connect: navEntry.connectEnd - navEntry.connectStart
            }
          });
        }
      }
    });
    try {
      observer.observe({ entryTypes: ['navigation'] });
    } catch (error) {
      void error;
    }
    return () => {
      observer.disconnect();
    };
  }, [enabled, logPerformance]);

  useEffect(() => {
    if (!enabled) return;
    const handleBeforeUnload = (): void => {
      if (eventQueueRef.current.length > 0) {
        const authToken = getAuthToken();
        if (authToken) {
          const payload: TelemetryBatchRequest = {
            events: [...eventQueueRef.current],
            metadata: {
              source: 'web',
              sdkVersion: '1.0.0'
            }
          };
          fetch(`${apiUrl}/events`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(payload),
            keepalive: true
          }).catch(() => void 0);
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, apiUrl]);

  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    };
  }, []);

  return {
    logError,
    logActivity,
    logPerformance,
    logBusinessEvent,
    flush,
    isEnabled: enabled
  };
};
